/* ============================================================================
   kumii-newsletter.js — "welcome back" for returning newsletter sign-ups.

   Why this exists: Shopify's `{% form 'customer' %}` newsletter form returns
   `posted_successfully` even when the email is ALREADY registered — it does not
   error on a duplicate. So the storefront has no server signal to tell a brand-new
   subscriber apart from a returning one, and everyone sees the "we'll email your
   code shortly" success message (which is wrong for people who already signed up).

   Since there is no server signal (short of a paid email-marketing app), we detect
   returning sign-ups at the DEVICE level: we remember which emails this browser has
   already submitted, and after the post-submit page reload we swap the success copy
   to the localized "already subscribed / welcome back" message.

   Scope / limits (by design): this catches the common real case — the same person
   re-submitting on the same browser. It cannot know about sign-ups made on another
   device or before localStorage was seeded; those still see the normal success copy.

   Covers BOTH sign-up surfaces: the subscription popup + newsletter section (the
   `newsletter-form` snippet) and the footer band (its own inline `{% form %}`).
   ============================================================================ */
(function () {
  'use strict';

  var EMAILS_KEY = 'kumii_news_emails'; // localStorage: emails this browser submitted
  var STATUS_KEY = 'kumii_news_status'; // sessionStorage: 'new' | 'returning' for the reload

  function norm(v) { return (v || '').trim().toLowerCase(); }

  function readEmails() {
    try { return JSON.parse(localStorage.getItem(EMAILS_KEY)) || []; }
    catch (e) { return []; }
  }
  function writeEmails(list) {
    try { localStorage.setItem(EMAILS_KEY, JSON.stringify(list)); } catch (e) {}
  }

  // A newsletter form is any Shopify customer form carrying the `newsletter` tag.
  function isNewsletterForm(form) {
    if (!form || form.nodeName !== 'FORM') return false;
    var tag = form.querySelector('input[name="contact[tags]"]');
    return !!(tag && /(^|,)\s*newsletter\s*(,|$)/i.test(tag.value || ''));
  }

  // 1) On submit (capture phase → runs before the form navigates away), record the
  //    email and whether it's a repeat. Storage writes are synchronous, so they
  //    survive the full-page reload that Shopify does next.
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!isNewsletterForm(form)) return;
    var input = form.querySelector('input[type="email"], input[name="contact[email]"]');
    var email = norm(input && input.value);
    if (!email) return;
    try {
      var list = readEmails();
      if (list.indexOf(email) !== -1) {
        sessionStorage.setItem(STATUS_KEY, 'returning');
      } else {
        sessionStorage.setItem(STATUS_KEY, 'new');
        list.push(email);
        writeEmails(list);
      }
    } catch (e) { /* private mode / storage blocked → degrade to normal success copy */ }
  }, true);

  // 2) After the reload, if the just-submitted email was a repeat, replace the
  //    success message text with the localized "welcome back" copy.
  function applyReturning() {
    var status = null;
    try { status = sessionStorage.getItem(STATUS_KEY); } catch (e) {}
    if (status) { try { sessionStorage.removeItem(STATUS_KEY); } catch (e) {} }
    if (status !== 'returning') return;

    var src = document.getElementById('kumii-news-returning-msg');
    var msg = src ? (src.textContent || '').trim() : '';
    if (!msg) return;

    // Popup + newsletter-section success message (keeps its leading icon).
    document.querySelectorAll('.newsletter-form .alert--success').forEach(function (el) {
      swapText(el, msg);
    });
    // Footer inline success note (plain text, no icon).
    document.querySelectorAll('.kumii-footer__note--ok').forEach(function (el) {
      el.textContent = msg;
    });
  }

  // Replace an element's text while preserving a leading <svg> icon if present.
  function swapText(el, msg) {
    var icon = el.querySelector('svg');
    el.textContent = '';
    if (icon) { el.appendChild(icon); el.appendChild(document.createTextNode(' ')); }
    el.appendChild(document.createTextNode(msg));
  }

  if (document.readyState !== 'loading') applyReturning();
  else document.addEventListener('DOMContentLoaded', applyReturning);
})();
